// Treatment plans: backend rows → the design's vocabulary (state/treatments.tsx,
// state/plans.tsx, state/instructions.tsx), plus the loaders the treatment
// screens share. Used by AllPlans, PlanView, TreatmentTypes, StatusDefinitions,
// InstructionTemplates, InstructionTemplateForm and PatientView.
import { apiGet } from "../crm/contactsKit";
import { localDay } from "../../lib/home";

/** Swatches offered for a treatment colour (design: TREATMENT_COLORS). */
export const TREATMENT_COLORS = ["#82ADD4", "#2F5F8A", "#DDFD2C", "#000000", "#5F5F5A", "#7FB89A", "#E8A87C", "#C97B84", "#9B8EC4", "#E5C46B"];

/* ---------- Treatment types ---------- */

/** /api/treatments row → TreatmentType. */
export const toTreatment = (row) => ({
  id: row.id,
  name: row.name || "",
  category: row.category || "",
  durationDays: row.default_duration_days === null || row.default_duration_days === undefined ? null : Number(row.default_duration_days),
  color: row.color || "#82ADD4",
  description: row.description || "",
  active: row.is_active === undefined ? true : !!Number(row.is_active),
  createdAt: row.created_at ? new Date(row.created_at).getTime() : null,
});

/** Active treatment types (GET /api/treatments?is_active=true). */
export async function loadTreatments() {
  const r = await apiGet("/api/treatments", { is_active: "true", limit: 500 });
  return r?.success ? (r.data || []).map(toTreatment) : null;
}

/* ---------- Status definitions ---------- */

/**
 * treatment_statuses row → PlanStatus. `key` is the stored name plans keep in
 * current_status; `name` is the label people read.
 */
export const toStatus = (row) => ({
  id: row.id,
  key: row.name,
  name: row.label || row.name || "",
  order: Number(row.sort_order) || 0,
  treatmentId: row.treatment_id || null,
  scope: row.treatment_id ? { treatmentIds: [row.treatment_id] } : "general",
  isInitial: !!Number(row.is_initial),
  isTerminal: !!Number(row.is_terminal),
  color: row.color || "#6b7280",
  description: row.description || "",
  // Stored, but the backend sends nothing for it (see StatusDefinitions).
  notify: !!Number(row.notify_patient),
  notifyTemplate: row.waba_template_name || "",
});

/** Every active status (GET /api/treatments/statuses/all). */
export async function loadStatuses() {
  const r = await apiGet("/api/treatments/statuses/all");
  return r?.success ? (r.data || []).map(toStatus) : null;
}

export const isGeneral = (status) => status.scope === "general";

/** A treatment's flow: general statuses plus its own, by order (general first on ties). */
export const flowFor = (statuses, treatmentId) =>
  statuses
    .filter((s) => isGeneral(s) || String(s.treatmentId) === String(treatmentId))
    .sort((a, b) => a.order - b.order || (isGeneral(a) ? -1 : isGeneral(b) ? 1 : 0) || a.name.localeCompare(b.name));

/** "Completed" / "Cancelled" style end states. */
export const isDone = (status) => !!status && (status.isTerminal || /^(completed|cancelled)$/i.test(status.name));
export const isCompleted = (status) => !!status && /complete/i.test(`${status.name} ${status.key}`);
export const isCancelled = (status) => !!status && /cancel|discontinu/i.test(`${status.name} ${status.key}`);
export const isOnHold = (status) => !!status && /hold/i.test(`${status.name} ${status.key}`);

/** The status a plan is in, from its stored current_status. */
export const statusOfPlan = (statuses, plan) => {
  const flow = flowFor(statuses, plan.treatmentId);
  return flow.find((s) => s.key === plan.statusKey) || flow.find((s) => s.name.toLowerCase() === String(plan.statusKey || "").toLowerCase()) || null;
};
export const statusLabel = (statuses, plan) => statusOfPlan(statuses, plan)?.name || (plan.statusKey ? String(plan.statusKey).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Unknown");

/* ---------- Plans ---------- */

/** /api/patient-treatments row → TreatmentPlan. */
export const toPlan = (row) => ({
  id: row.id,
  code: row.plan_number || `#${row.id}`,
  patientId: row.patient_id,
  patientName: [row.patient_first_name, row.patient_last_name].filter(Boolean).join(" ").trim(),
  patientPhone: row.patient_phone || "",
  patientEmail: row.patient_email || "",
  treatmentId: row.treatment_id,
  treatmentName: row.treatment_name || "",
  treatmentColor: row.treatment_color || "",
  doctorId: row.doctor_id || "",
  doctor: row.doctor_name || "",
  facilityId: row.facility_id || "",
  facility: row.facility_name || "",
  statusKey: row.current_status || "",
  startDate: localDay(row.start_date),
  expectedEnd: localDay(row.expected_end_date),
  endedAt: localDay(row.actual_end_date),
  notes: row.notes || "",
  templateId: row.instruction_template_id || null,
  sendWhatsApp: !!Number(row.send_whatsapp_instructions),
  createdAt: row.created_at ? new Date(row.created_at).getTime() : null,
  createdById: row.created_by || null,
});

/** The progress line: non-final steps of the flow, and how many are done. */
export function progressOf(statuses, plan) {
  const flow = flowFor(statuses, plan.treatmentId).filter((s) => !isDone(s));
  const current = statusOfPlan(statuses, plan);
  const index = flow.findIndex((s) => current && s.id === current.id);
  const done = isCompleted(current) ? flow.length : Math.max(index + 1, 0);
  return { flow, done, current };
}

/* ---------- Instruction templates ---------- */

export const TYPE_LABEL = { status_based: "Status based", day_wise: "Day wise", general: "General" };
export const TYPE_KEY = { "Status based": "status_based", "Day wise": "day_wise", General: "general" };

/** Template variable tokens (GET /api/instruction-templates/variables) under the design's names where they match. */
export const DESIGN_NAME = {
  patient_first_name: "Patient first name",
  patient_full_name: "Patient full name",
  treatment_name: "Treatment name",
  doctor_name: "Doctor name",
  org_name: "Clinic name",
  current_status: "Current status",
};
/** "Fixed value" is the step's own text, which the backend fills in for the `instruction` token. */
export const FIXED = "Fixed value";
export const FIXED_TOKEN = "instruction";

/** Variable sources for the picker: label ↔ token. */
export function variableSources(apiVariables) {
  const rows = (apiVariables || []).filter((v) => v.token !== FIXED_TOKEN).map((v) => ({ token: v.token, label: DESIGN_NAME[v.token] || v.label }));
  return [...rows, { token: FIXED_TOKEN, label: FIXED }];
}

/** Highest {{n}} in a WhatsApp template body. */
export const placeholderCount = (body) => {
  const nums = [...String(body || "").matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
  return nums.length ? Math.max(...nums) : 0;
};

const parseJson = (value, fallback) => {
  if (Array.isArray(value) || (value && typeof value === "object")) return value;
  if (typeof value === "string" && value.trim()) { try { return JSON.parse(value); } catch (_) { return fallback; } }
  return fallback;
};
const strip = (html) => String(html || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").trim();

/**
 * A stored mapping → the design's variables. A slot is a token, or
 * { fixed: "text" } (item 197: each placeholder may have its own fixed text).
 * The older `instruction` token fills a slot with the row's own text, so it
 * reads back as a fixed value holding that text.
 */
function toVariables(tokens, text, sources) {
  const list = parseJson(tokens, []);
  return (Array.isArray(list) ? list : []).map((token) => {
    if (token && typeof token === "object") return { source: FIXED, value: typeof token.fixed === "string" ? token.fixed : "" };
    if (token === FIXED_TOKEN) return { source: FIXED, value: text };
    const src = sources.find((s) => s.token === token);
    return { source: src ? src.label : "" };
  });
}

/** The design's variables → the stored mapping: tokens, and { fixed } for fixed values. */
export function toMapping(variables, sources) {
  return (variables || []).map((v) => (v.source === FIXED ? { fixed: String(v.value ?? "").trim() } : sources.find((s) => s.label === v.source)?.token || null));
}

const hhmm = (value) => (value ? String(value).slice(0, 5) : "");

/**
 * A day, trigger or general-step row → a Step. `trigger` is built by the
 * caller; `sendAt` is "HH:MM", or "" for a general step sent straight away.
 */
function rowToStep(row, trigger, sources, sendAt, id) {
  const text = strip(row.content_plain || row.content_html);
  const call = !!Number(row.voice_call_enabled);
  const fallbackOn = Number(row.fallback_whatsapp) === 1;
  return {
    id,
    trigger,
    sendAt,
    channel: call ? "Call" : "WhatsApp",
    whatsapp: call ? undefined : { templateId: row.waba_template_name || "", variables: toVariables(row.waba_template_params, text, sources) },
    call: call ? {
      script: row.voice_call_script || "",
      // Items 195–196: retry is on unless switched off; the fallback template is the step's own.
      retryIfNoAnswer: row.voice_call_retry === undefined || row.voice_call_retry === null ? true : Number(row.voice_call_retry) !== 0,
      fallbackToWhatsApp: fallbackOn,
      fallback: { templateId: row.fallback_waba_template_name || "", variables: toVariables(row.fallback_waba_template_params, text, sources) },
    } : undefined,
    // What the row says in plain text (e.g. a message Olli drafted before a WhatsApp template is picked).
    text,
    raw: row,
  };
}

const TEMPLATE_STATUS = { draft: "Draft", live: "Live", paused: "Paused" };
/** The design's status → the backend's (item 191). */
export const STATUS_KEY = { Draft: "draft", Live: "live", Paused: "paused" };

/**
 * GET /api/instruction-templates/:id → InstructionTemplate (design shape).
 * Status-based steps point at status ids. Also takes an Olli draft
 * (POST /draft-from-prompt), whose rows have no ids.
 */
export function toTemplate(row, statuses, sources) {
  const type = TYPE_LABEL[row.template_type] || "General";
  const key = row.id ?? "draft";
  let steps = [];
  if (row.template_type === "day_wise") {
    steps = (row.days || []).map((d, i) => rowToStep(d, { kind: "day", day: Number(d.day_number) - 1 }, sources, hhmm(d.send_time) || "09:00", `row-${d.id ?? `${key}-d${i}`}`));
  } else if (row.template_type === "status_based") {
    const flow = flowFor(statuses, row.treatment_id);
    steps = (row.triggers || []).map((t, i) => {
      const st = flow.find((s) => s.key === t.trigger_status_name) || statuses.find((s) => s.key === t.trigger_status_name);
      // A trigger without a time has always gone out at 09:00 (item 193).
      return rowToStep(t, { kind: "status", statusId: st ? st.id : "" }, sources, hhmm(t.send_time) || "09:00", `row-${t.id ?? `${key}-t${i}`}`);
    });
  } else {
    // General: a list of steps (item 194), or the original single message.
    const list = parseJson(row.general_steps, null);
    steps = Array.isArray(list) && list.length
      ? list.map((g, i) => rowToStep(g || {}, { kind: "general" }, sources, hhmm(g?.send_time) || hhmm(row.send_time), `general-${key}-${i}`))
      : [rowToStep({ ...row, voice_call_enabled: 0 }, { kind: "general" }, sources, hhmm(row.send_time), `general-${key}`)];
  }
  return {
    id: row.id ?? null,
    name: row.name || "",
    treatmentId: row.treatment_id || "",
    type,
    status: TEMPLATE_STATUS[row.status] || "Live",
    steps,
    createdBy: "",
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || row.created_at || "",
    raw: row,
  };
}

/** WhatsApp templates the clinic can send (GET /api/waba/templates?status=approved). */
export async function loadWaTemplates() {
  const r = await apiGet("/api/waba/templates", { status: "approved", limit: 500 });
  const rows = r?.templates || r?.data || [];
  return rows.map((t) => ({ id: t.template_name, name: t.template_name, category: t.category ? String(t.category).toLowerCase().replace(/^./, (c) => c.toUpperCase()) : "", body: t.body_text || "", language: t.language || "" })).filter((t) => t.id);
}

/** The clinic's name (GET /api/organizations/my-organizations). */
export async function loadClinicName() {
  const r = await apiGet("/api/organizations/my-organizations");
  let orgId = null;
  try { orgId = JSON.parse(localStorage.getItem("user") || "{}")?.organization_id ?? null; } catch (_) { orgId = null; }
  const orgs = r?.data || [];
  return (orgs.find((o) => o.id === orgId) || orgs[0])?.name || "";
}

const hintIds = (value) => { const v = parseJson(value, []); return Array.isArray(v) ? v : []; };
const rowCopy = (r) => ({ ...r, waba_template_params: parseJson(r.waba_template_params, null), fallback_waba_template_params: parseJson(r.fallback_waba_template_params, null), voice_call_hint_ids: hintIds(r.voice_call_hint_ids) });

/**
 * A template row from GET /api/instruction-templates/:id → a POST body for a
 * copy. The copy starts as a draft, so it can't message patients alongside the
 * original until someone approves it (item 191).
 */
export function copyPayload(row, name) {
  const general = parseJson(row.general_steps, null);
  return {
    name, treatment_id: row.treatment_id || null, description: row.description || null, template_type: row.template_type, instruction_type: row.instruction_type || "general",
    content_html: row.content_html || null, trigger_status_name: row.trigger_status_name || null, trigger_days_offset: row.trigger_days_offset || 0,
    send_whatsapp: !!Number(row.send_whatsapp), waba_template_name: row.waba_template_name || null, waba_template_params: parseJson(row.waba_template_params, null), language: row.language || "English",
    status: "draft", send_time: row.send_time || null,
    ...(Array.isArray(general) && general.length ? { general_steps: general.map(rowCopy) } : {}),
    days: (row.days || []).map(rowCopy), triggers: (row.triggers || []).map(rowCopy),
  };
}
